import { useState, useEffect, useCallback, useRef } from "react";
import { C, BASE } from './constants';
import DatePicker from './components/DatePicker';
import Sidebar from './components/Sidebar';
import CatModal from './components/CatModal';
import OverviewPage from './pages/OverviewPage';
import ProductsPage from './pages/ProductsPage';
import OrdersPage from './pages/OrdersPage';
import ProfitPage from './pages/ProfitPage';
import ProblemsPage from './pages/ProblemsPage';
import TrendingPage from './pages/TrendingPage';
import WarehousePage from './pages/WarehousePage';
import CompetitorsPage from './pages/CompetitorsPage';
import TasksPage from './pages/TasksPage';
import TaskModal from './components/TaskModal';
import AdsPage from './pages/AdsPage';
import InsightsPage from './pages/InsightsPage';
import KitsPage from './pages/KitsPage';
import { ProductNavContext } from './components/ProductNavContext';

export default function RozetkaDashboard({ apiBaseUrl, onLogout, onOpenSettings, onOpenAdmin, hasCredentials, tenantId, hasEpicentr }) {
  const API = apiBaseUrl || BASE;
  const [data, setData] = useState(null); const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const tabFromUrl = () => {
    const p = window.location.pathname.replace("/", "") || "insights";
    return ["insights","overview","products","orders","profit","problems","trending","warehouse","competitors","tasks","ads","kits"].includes(p) ? p : "insights";
  };
  const [tab, setTab] = useState(tabFromUrl);
  const defaultDateTo = new Date().toISOString().slice(0, 10);
  const defaultDateFrom = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom); const [dateTo, setDateTo] = useState(defaultDateTo);
  const [isPreset, setIsPreset] = useState(true);
  const [catModal, setCatModal] = useState(null); const [catalogStats, setCatalogStats] = useState(null);
  const [reviewsMap, setReviewsMap] = useState({});
  const [allProducts, setAllProducts] = useState(null); const [allProductsLoading, setAllProductsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); const [abcFilter, setAbcFilter] = useState("all");
  const [prodSearch, setProdSearch] = useState(""); const [prodPage, setProdPage] = useState(1);
  const [prodPerPage, setProdPerPage] = useState(50); const [sortBy, setSortBy] = useState("product_id");
  const [sortDir, setSortDir] = useState("desc"); const [localCosts, setLocalCosts] = useState({});
  const [orders, setOrders] = useState(null); const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1); const [ordersFilter, setOrdersFilter] = useState(null);
  const [ordersSearch, setOrdersSearch] = useState("");
  const [deadCatFilter, setDeadCatFilter] = useState("all"); const [deadSort, setDeadSort] = useState("days_desc");
  const [criticalStockOnly, setCriticalStockOnly] = useState(false);
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [problemFilter, setProblemFilter] = useState(new Set());
  const [tasks, setTasks] = useState([]);
  const [taskMembers, setTaskMembers] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [quickTaskProduct, setQuickTaskProduct] = useState(null);
  const [balance, setBalance] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [pinnedIds, setPinnedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("crm_pinned") || "[]"); } catch { return []; }
  });
  const [windowWidth, setWindowWidth] = useState(() => typeof window !== "undefined" ? window.innerWidth : 1200);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = windowWidth < 768;
  const [marketplace, setMarketplace] = useState('rozetka');

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const load = useCallback(async (df, dt) => {
    setLoading(true); setError("");
    try {
      const token = localStorage.getItem("crm_token");
      const mpParam = `marketplace=${marketplace}`;
      const url = `${API}/api/dashboard` + (df && dt ? `?date_from=${df}&date_to=${dt}&${mpParam}` : `?${mpParam}`);
      const res = await fetch(url, { headers: { Authorization: "Bearer " + token } });
      if (res.status === 401) { localStorage.removeItem("crm_token"); onLogout?.(); return; }
      if (!res.ok) throw new Error("Помилка " + res.status);
      const json = await res.json();
      setData(json);
      // When 'Все' (no dates), sync dateFrom/dateTo from actual period returned by backend
      if (!df || !dt) {
        if (json.period?.from) setDateFrom(json.period.from);
        if (json.period?.to) setDateTo(json.period.to);
      }
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [API, onLogout, marketplace]);

  const loadOrders = async (page, sg, df, dt, search, npProblem, delivery) => {
    setOrdersLoading(true);
    try {
      const token = localStorage.getItem("crm_token");
      let url = `${API}/api/orders?page=${page}&per_page=50&marketplace=${marketplace}`;
      if (sg != null) url += `&status_group=${sg}`;
      if (df && dt) url += `&date_from=${df}&date_to=${dt}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (npProblem) url += `&np_problem=1`;
      if (delivery) url += `&delivery=${encodeURIComponent(delivery)}`;
      const res = await fetch(url, { headers: { Authorization: "Bearer " + token } });
      if (res.status === 401) { localStorage.removeItem("crm_token"); onLogout?.(); return; }
      setOrders(await res.json()); setOrdersPage(page);
    } catch (e) { console.error(e); } finally { setOrdersLoading(false); }
  };

  const loadCatalogStats = useCallback(async () => {
    const token = localStorage.getItem("crm_token");
    const res = await fetch(`${API}/api/catalog/stats`, { headers: { Authorization: "Bearer " + token } });
    if (res.ok) setCatalogStats(await res.json());
  }, [API]);

  const loadReviews = useCallback(async () => {
    const token = localStorage.getItem("crm_token");
    const res = await fetch(`${API}/api/reviews/summary`, { headers: { Authorization: "Bearer " + token } });
    if (res.ok) {
      const d = await res.json();
      const map = {};
      (d.reviews || []).forEach(r => { map[r.rozetka_id] = r; });
      setReviewsMap(map);
    }
  }, [API]);

  const loadProducts = useCallback(async () => {
    setAllProductsLoading(true);
    try {
      const token = localStorage.getItem("crm_token");
      const res = await fetch(`${API}/api/products?show_inactive=true`, { headers: { Authorization: "Bearer " + token } });
      if (res.ok) setAllProducts(await res.json());
    } catch (e) { console.error(e); } finally { setAllProductsLoading(false); }
  }, [API]);

  const applyDates = (df, dt, preset = false) => {
    setIsPreset(preset);
    setDateFrom(df); setDateTo(dt); load(df, dt);
    if (tab === "orders") loadOrders(1, ordersFilter, df, dt, ordersSearch || null);
  };

  const [syncOrdersLoading, setSyncOrdersLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const fetchSyncStatus = useCallback(async () => {
    const token = localStorage.getItem("crm_token");
    try {
      const res = await fetch(`${API}/api/sync/status`, { headers: { Authorization: "Bearer " + token } });
      if (res.ok) {
        const s = await res.json();
        setSyncStatus(s);
        if (!s.running) setSyncOrdersLoading(false);
      }
    } catch (_) {}
  }, [API]);

  // Poll sync status every 5s when running
  useEffect(() => {
    fetchSyncStatus();
    const id = setInterval(fetchSyncStatus, 5000);
    return () => clearInterval(id);
  }, [fetchSyncStatus]);

  // Reload dashboard when sync finishes
  const prevRunning = useRef(false);
  useEffect(() => {
    if (prevRunning.current && syncStatus && !syncStatus.running) {
      load(dateFrom, dateTo);
    }
    prevRunning.current = syncStatus?.running || false;
  }, [syncStatus]); // eslint-disable-line

  const syncCatalog = async () => {
    const token = localStorage.getItem("crm_token");
    await fetch(`${API}/api/sync/catalog`, { headers: { Authorization: "Bearer " + token } });
    setTimeout(loadCatalogStats, 3000);
  };

  const syncOrders = async () => {
    if (syncOrdersLoading) return;
    setSyncOrdersLoading(true);
    const token = localStorage.getItem("crm_token");
    await fetch(`${API}/api/sync`, { headers: { Authorization: "Bearer " + token } });
    fetchSyncStatus();
  };

  const loadTasks = async () => {
    setTasksLoading(true);
    try {
      const token = localStorage.getItem("crm_token");
      const res = await fetch(`${API}/api/tasks`, { headers: { Authorization: "Bearer " + token } });
      if (res.ok) {
        const j = await res.json();
        setTasks(j.tasks || []);
        setTaskMembers(j.members || []);
      }
    } catch (e) { console.error(e); } finally { setTasksLoading(false); }
  };

  const loadInsights = async () => {
    setInsightsLoading(true);
    try {
      const token = localStorage.getItem("crm_token");
      const res = await fetch(`${API}/api/insights`, { headers: { Authorization: "Bearer " + token } });
      if (res.ok) setInsightsData(await res.json());
    } catch (e) { console.error(e); } finally { setInsightsLoading(false); }
  };

  const handleSetTab = (id) => {
    setTab(id);
    window.history.pushState({}, "", "/" + id);
    if (id === "insights" && !insightsData) loadInsights();
    if (id === "orders" && !orders) loadOrders(1, null, dateFrom, dateTo, null);
    if ((id === "products" || id === "overview" || id === "kits") && !allProducts) loadProducts();
    if (id === "tasks") loadTasks();
  };

  const goToProductsWithFilter = (filter) => {
    setProblemFilter(new Set([filter]));
    handleSetTab("products");
  };

  const openProductInProducts = (rozetkaId) => {
    if (rozetkaId == null) return;
    setProblemsOnly(false);
    setProblemFilter(new Set());
    setStatusFilter("all");
    setAbcFilter("all");
    setCriticalStockOnly(false);
    setProdPage(1);
    setProdSearch(String(rozetkaId));
    handleSetTab("products");
  };

  useEffect(() => {
    const onPop = () => setTab(tabFromUrl());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []); // eslint-disable-line

  useEffect(() => {
    const token = localStorage.getItem("crm_token");
    fetch(`${API}/api/pinned`, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (j?.pinned) { setPinnedIds(j.pinned); localStorage.setItem("crm_pinned", JSON.stringify(j.pinned)); }})
      .catch(() => {});
  }, [API]); // eslint-disable-line

  const togglePin = useCallback(async (productId) => {
    const token = localStorage.getItem("crm_token");
    const isPinned = pinnedIds.includes(productId);
    const url = `${API}/api/pinned/${productId}`;
    const method = isPinned ? "DELETE" : "POST";
    try {
      const res = await fetch(url, { method, headers: { Authorization: "Bearer " + token } });
      if (res.ok) {
        const next = isPinned ? pinnedIds.filter(id => id !== productId) : [...pinnedIds, productId];
        setPinnedIds(next);
        localStorage.setItem("crm_pinned", JSON.stringify(next));
      }
    } catch (_) {}
  }, [API, pinnedIds]);

  useEffect(() => { load(defaultDateFrom, defaultDateTo); }, []); // eslint-disable-line
  useEffect(() => { loadCatalogStats(); }, []); // eslint-disable-line
  useEffect(() => { loadReviews(); }, []); // eslint-disable-line
  useEffect(() => { if (tab === "products" || tab === "overview" || tab === "kits") loadProducts(); }, []); // eslint-disable-line
  useEffect(() => { loadTasks(); }, []); // eslint-disable-line
  useEffect(() => { loadInsights(); }, []); // eslint-disable-line
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const token = localStorage.getItem("crm_token");
        const res = await fetch(`${API}/api/balance`, { headers: { Authorization: "Bearer " + token } });
        if (res.ok) { const j = await res.json(); setBalance(j.data); }
      } catch (_) {}
    };
    fetchBalance();
    const id = setInterval(fetchBalance, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(id);
  }, [API]); // eslint-disable-line
  useEffect(() => { setProdPage(1); }, [statusFilter, abcFilter, prodSearch, prodPerPage]);
  useEffect(() => {
    if (tab !== "orders") return;
    const t = setTimeout(() => loadOrders(1, ordersFilter, dateFrom, dateTo, ordersSearch || null), 400);
    return () => clearTimeout(t);
  }, [ordersSearch, tab]); // eslint-disable-line
  useEffect(() => {
    load(dateFrom, dateTo);
    if (tab === "orders") loadOrders(1, ordersFilter, dateFrom, dateTo, ordersSearch || null);
  }, [marketplace]); // eslint-disable-line

  if (loading && !data) return (
    <div style={{ background: C.bg0, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "Inter,sans-serif", gap: 20 }}>
      <div style={{ position: "relative", width: 56, height: 56 }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `3px solid ${C.blue}22` }} />
        <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `3px solid transparent`, borderTopColor: C.blue, animation: "spin 0.8s linear infinite" }} />
        <div style={{ position: "absolute", inset: 8, borderRadius: "50%", border: `2px solid transparent`, borderTopColor: C.green, animation: "spin 1.2s linear infinite reverse" }} />
      </div>
      <div style={{ color: C.text2, fontSize: 14, fontWeight: 500, letterSpacing: "0.02em" }}>Завантаження аналітики...</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
  if ((error && !data) || (!data && !loading)) return (
    <div style={{ background: C.bg0, color: C.pink, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, fontFamily: "Inter,sans-serif" }}>
      <div>Помилка: {error || "Немає даних"}</div>
      <button onClick={() => { localStorage.removeItem("crm_token"); onLogout?.(); }} style={{ padding: "10px 24px", background: C.blue, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}>Увійти знову</button>
    </div>
  );

  const kpi = data.kpi || {}, prevKpi = data.kpi_prev || null;
  const abc = data.abc_analysis || [], dead = data.dead_stock || [];
  const trend = data.trending || [], daily = data.daily || [], cats = data.categories || [];
  const revivedStock = data.revived_stock || [];
  const pinnedData = data.pinned_data || [];
  const ffBypass = data.ff_bypass || [];
  const loss = data.loss_items || [];
  const realDead = dead.filter(p => p.stock > 0);
  const CHART_COLORS = [C.blue, C.green, C.yellow, C.pink, C.purple, "#fb923c", "#22d3ee", "#a3e635"];
  const negativeReviews = allProducts ? (allProducts.products || []).filter(p =>
    p.status !== "inactive" && reviewsMap[p.rozetka_id] &&
    reviewsMap[p.rozetka_id].top_rating !== null && reviewsMap[p.rozetka_id].top_rating <= 3
  ).map(p => ({ product: p, review: reviewsMap[p.rozetka_id] })) : [];

  const allProds = allProducts?.products || [];
  const outOfStockProds = allProds.filter(p => p.is_ff && (p.ff_stock || 0) === 0 && p.status !== "inactive");
  const blockedProds = allProds.filter(p => p.upload_status === 11);
  const notInPromoProds = allProds.filter(p =>
    (p.promo_required_price || 0) > 0 &&
    ((p.catalog_promo_price || 0) === 0 || (p.catalog_promo_price || 0) > p.promo_required_price)
  );
  const abcWithCost = abc.filter(p => p.cost_price > 0);
  const profitKpi = abcWithCost.length > 0 ? (() => {
    const total = abcWithCost.reduce((s, p) => s + (p.profit_total || 0), 0);
    const rev = abcWithCost.reduce((s, p) => s + (p.avg_price || 0) * (p.quantity || 0), 0);
    return { total, covered: abcWithCost.length, margin: rev > 0 ? total / rev * 100 : null };
  })() : null;
  const problemCount = loss.length + realDead.length + ffBypass.length + negativeReviews.length;
  const tasksMap = {};
  tasks.forEach(t => { if (t.product_rozetka_id) tasksMap[t.product_rozetka_id] = (tasksMap[t.product_rozetka_id] || 0) + 1; });
  const insightsUrgentCount = insightsData?.meta?.urgent_count || null;
  const nav = [
    { id: "insights", icon: "💡", label: "Інсайти", badge: insightsUrgentCount || null },
    { id: "overview", icon: "◈", label: "Огляд" }, { id: "products", icon: "▦", label: "Товари" },
    { id: "orders", icon: "≡", label: "Замовлення" }, { id: "profit", icon: "◎", label: "Прибутковість" },
{ id: "trending", icon: "🔥", label: "Зростання", badge: trend.filter(p => p.was_revived).length || null },
    { id: "problems", icon: "⚠", label: "Проблеми", badge: problemCount },
    { id: "warehouse", icon: "🏭", label: "Склад" },
    { id: "competitors", icon: "⚔", label: "Конкуренти" },
    { id: "tasks", icon: "☑", label: "Задачі", badge: tasks.filter(t => t.status !== "done").length || null },
    { id: "ads", icon: "📢", label: "Реклама" },
    { id: "kits", icon: "🎁", label: "Комплекти" },
  ];

  return (
    <ProductNavContext.Provider value={{ openProductInProducts }}>
    <div style={{ background: C.bg0, color: C.text1, fontFamily: "'Inter','Manrope',sans-serif", height: "100vh", overflow: "hidden", display: "flex" }}>
      <Sidebar tab={tab} setTab={handleSetTab} nav={nav} data={data} catalogStats={catalogStats} syncCatalog={syncCatalog} syncOrders={syncOrders} syncOrdersLoading={syncOrdersLoading || syncStatus?.running} onLogout={onLogout} onSettings={onOpenSettings} onAdmin={tenantId === 1 ? onOpenAdmin : null} hasCredentials={hasCredentials} balance={balance} isMobile={isMobile} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
      <div style={{ flex: 1, overflow: "auto", minWidth: 0 }}>
        {syncStatus?.running && (() => {
          const { page, total, total_pages: totalPages, synced } = syncStatus;
          const pct = totalPages > 0 ? Math.min(100, Math.round((page / totalPages) * 100)) : 0;
          return (
            <div style={{ background: "#0f2a1a", borderBottom: `1px solid ${C.green}44`, padding: "8px 24px", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 13, color: C.green, fontWeight: 600 }}>
                ⏳ Синхронізація замовлень...
              </span>
              <div style={{ flex: 1, maxWidth: 300, height: 6, background: "#1a3a2a", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: C.green, borderRadius: 3, transition: "width .5s" }} />
              </div>
              <span style={{ fontSize: 12, color: C.text3 }}>
                {total > 0 ? `${pct}% · сторінка ${page} з ${totalPages} · ${synced.toLocaleString()} / ${total.toLocaleString()}` : "завантаження..."}
              </span>
            </div>
          );
        })()}
        {/* Mobile hamburger */}
        <button
          className="crm-hamburger"
          onClick={() => setMobileOpen(o => !o)}
          style={{
            position: "fixed", top: 12, left: 12, zIndex: 198,
            width: 38, height: 38, borderRadius: 10,
            background: C.bg2, border: `1px solid ${C.border}`,
            color: C.text1, fontSize: 18, cursor: "pointer",
            alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 12px rgba(0,0,0,.4)",
          }}
        >☰</button>
        {hasEpicentr && (
          <div style={{ padding: "6px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 4, background: C.bg1, alignItems: "center" }}>
            <span style={{ fontSize: 11, color: C.text3, marginRight: 4 }}>Маркетплейс:</span>
            {[['rozetka', 'Rozetka'], ['epicentr', 'Epicentr'], ['all', 'Всі']].map(([mp, label]) => (
              <button key={mp} onClick={() => setMarketplace(mp)} style={{
                padding: "3px 10px", borderRadius: 5, fontSize: 12, cursor: "pointer",
                fontWeight: marketplace === mp ? 700 : 400,
                background: marketplace === mp ? C.blue : C.bg2,
                color: marketplace === mp ? "#fff" : C.text2,
                border: `1px solid ${marketplace === mp ? C.blue : C.border}`,
              }}>{label}</button>
            ))}
          </div>
        )}
        {!["insights", "products", "problems", "trending", "warehouse", "overview", "tasks"].includes(tab) && (
          <div style={{ padding: "12px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: `linear-gradient(180deg, ${C.bg1} 0%, ${C.bg0} 100%)`, flexWrap: "wrap", gap: 10 }}>
            <DatePicker from={dateFrom || data?.period?.from || ""} to={dateTo || data?.period?.to || ""} onApply={applyDates} />
          </div>
        )}
        <div className="crm-content">
          {tab === "insights" && <InsightsPage insightsData={insightsData} insightsLoading={insightsLoading} onRefresh={loadInsights} onNav={handleSetTab} />}
          {tab === "overview" && <OverviewPage kpi={kpi} prevKpi={prevKpi} daily={daily} cats={cats} trend={trend} dead={dead} realDead={realDead} dateFrom={dateFrom} dateTo={dateTo} isPreset={isPreset} onApplyDates={applyDates} CHART_COLORS={CHART_COLORS} setCatModal={setCatModal} data={data} loss={loss} negativeReviews={negativeReviews} profitKpi={profitKpi} abc={abc} ffBypass={ffBypass} outOfStockProds={outOfStockProds} blockedProds={blockedProds} notInPromoProds={notInPromoProds} goToProductsWithFilter={goToProductsWithFilter} revivedStock={revivedStock} pinnedData={pinnedData} reviewDaily={data.review_daily || []} marketplace={marketplace} />}
          {tab === "products" && <ProductsPage allProducts={allProducts} allProductsLoading={allProductsLoading} statusFilter={statusFilter} setStatusFilter={setStatusFilter} abcFilter={abcFilter} setAbcFilter={setAbcFilter} prodSearch={prodSearch} setProdSearch={setProdSearch} prodPage={prodPage} setProdPage={setProdPage} prodPerPage={prodPerPage} setProdPerPage={setProdPerPage} sortBy={sortBy} setSortBy={setSortBy} sortDir={sortDir} setSortDir={setSortDir} localCosts={localCosts} setLocalCosts={setLocalCosts} isMobile={isMobile} criticalStockOnly={criticalStockOnly} setCriticalStockOnly={setCriticalStockOnly} problemsOnly={problemsOnly} setProblemsOnly={setProblemsOnly} realDead={realDead} reviewsMap={reviewsMap} problemFilter={problemFilter} setProblemFilter={setProblemFilter} pinnedIds={pinnedIds} togglePin={togglePin} onTaskCreate={setQuickTaskProduct} tasksMap={tasksMap} />}
          {tab === "orders" && <OrdersPage orders={orders} ordersLoading={ordersLoading} ordersPage={ordersPage} ordersFilter={ordersFilter} ordersSearch={ordersSearch} setOrdersFilter={setOrdersFilter} setOrdersSearch={setOrdersSearch} loadOrders={loadOrders} dateFrom={dateFrom} dateTo={dateTo} API={API} onLogout={onLogout} />}
          {tab === "profit" && <ProfitPage abc={abc} loss={loss} dateFrom={dateFrom || data?.period?.from} dateTo={dateTo || data?.period?.to} />}
{tab === "trending" && <TrendingPage trend={trend} revivedStock={revivedStock} />}
          {tab === "problems" && <ProblemsPage realDead={realDead} loss={loss} ffBypass={ffBypass} deadCatFilter={deadCatFilter} setDeadCatFilter={setDeadCatFilter} deadSort={deadSort} setDeadSort={setDeadSort} negativeReviews={negativeReviews} />}
          {tab === "warehouse" && <WarehousePage />}
          {tab === "competitors" && <CompetitorsPage />}
          {tab === "tasks" && <TasksPage tasks={tasks} members={taskMembers} tasksLoading={tasksLoading} token={localStorage.getItem("crm_token")} reload={loadTasks} />}
          {tab === "ads" && <AdsPage token={localStorage.getItem("crm_token")} />}
          {tab === "kits" && <KitsPage token={localStorage.getItem("crm_token")} allProducts={allProducts} isMobile={isMobile} />}
        </div>
      </div>
      {catModal && <CatModal name={catModal} dateFrom={dateFrom} dateTo={dateTo} onClose={() => setCatModal(null)} />}
      {quickTaskProduct !== null && (
        <TaskModal
          task={{ product_rozetka_id: quickTaskProduct.product_id, product_name: quickTaskProduct.name, product_image: quickTaskProduct.image_url || "" }}
          members={taskMembers}
          token={localStorage.getItem("crm_token")}
          onSave={async (data) => {
            await fetch(`${API}/api/tasks`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("crm_token") },
              body: JSON.stringify(data),
            });
            setQuickTaskProduct(null);
            loadTasks();
          }}
          onClose={() => setQuickTaskProduct(null)}
        />
      )}
    </div>
    </ProductNavContext.Provider>
  );
}
